import { BreakpointObserver } from '@angular/cdk/layout';
import { Component, Inject, LOCALE_ID, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, Subscription } from 'rxjs';
import * as URI from 'urijs';

import { State, selectCurrentUser, selectTenant } from '®app/ngrx/state';
import { User } from '®shared/auth';
import { AppConfig } from '®shared/config';
import {
  CurrentUserAndPermissions,
  CurrentUserService,
} from '®shared/current-user';
import { Loadable } from '®shared/data';
import { Locale, getDetailsForLocales } from '®shared/locale/locale-utils';
import {
  Permissions,
  UserPermissions,
  createDefaultUserPermissions,
} from '®shared/permissions';
import { RedirectionService } from '®shared/redirection';
import { Tenant } from '®shared/tenant';
import { UriService } from '®shared/url-utils';

export const MEDIUM_SCREEN_BREAKPOINT = '(min-width: 768px)';

@Component({
  selector: 'rl-navbar',
  templateUrl: './navbar.component.html',
})
export class NavbarComponent implements OnInit, OnDestroy {
  isCollapsed = true;
  tenant$: Observable<Tenant | null>;
  permissions: UserPermissions;
  user: User | null = null;

  tenantName = this.appConfig.tenant.name;
  reviewsUrl = this.appConfig.tenant.reviewsUrl;
  tenantShortCode = this.appConfig.tenant.shortCode;
  isCourseFlowTenancy = this.appConfig.tenant.featureFlags.isCourseFlowTenancy;

  private breakpointSubscription: Subscription;
  private currentUserSubscription: Subscription;
  private tenantSubscription: Subscription;
  createProfileUrl: string;
  originPageRedirectUrl: string;
  activeLocales: Locale[];

  constructor(
    private appConfig: AppConfig,
    private breakpointObserver: BreakpointObserver,
    private currentUserService: CurrentUserService,
    @Inject(LOCALE_ID) public currentLocale: string,
    private redirectionService: RedirectionService,
    private uriService: UriService,
    private store: Store<State>,
  ) {
    this.permissions = createDefaultUserPermissions(
      appConfig.tenant.roleCodesByRoleId,
    );

    this.breakpointSubscription = this.breakpointObserver
      .observe([MEDIUM_SCREEN_BREAKPOINT])
      .subscribe(result => {
        this.isCollapsed = !result.matches;
      });
  }

  ngOnDestroy() {
    if (this.currentUserSubscription) {
      this.currentUserSubscription.unsubscribe();
    }
    if (this.breakpointSubscription) {
      this.breakpointSubscription.unsubscribe();
    }
    if (this.tenantSubscription) {
      this.tenantSubscription.unsubscribe();
    }
  }

  ngOnInit() {
    this.tenant$ = this.store.select(selectTenant);
    this.tenantSubscription = this.tenant$.subscribe((tenant: Tenant) => {
      if (tenant) {
        this.activeLocales = getDetailsForLocales(tenant.active_locales);
      }
    });

    this.store
      .select(selectCurrentUser)
      .forEach((user: Loadable<CurrentUserAndPermissions>) => {
        if (user && user.data) {
          this.user = user.data.oauthUser;
          this.permissions = user.data.permissions;
        }
      });

    this.createProfileUrl = `/3/${this.appConfig.tenant.shortCode}/my_profile/edit.html`;
    this.originPageRedirectUrl = this.generateCreateProfileRedirectUrl();
  }

  /**
   * Wrapper method to generate a classic TARL URL, with appropriate flags to log a user in correctly
   * @param {string} url Relative URL
   */
  generateClassicUrl(url: string = '/index.html'): string {
    const uri = new URI(url);
    return this.uriService
      .getClassicUri(
        uri.is('relative')
          ? uri
          : uri.relativeTo(this.appConfig.tenant.conceptBaseUri),
      )
      .toString();
  }

  /**
   * Wrapper method to generate a classic TARL URL for Feedback page
   * with appropriate flags, including origin param
   */
  generateClassicFeedbackUrl(): string {
    const uri = new URI('/feedback');
    const classicUri = this.uriService.getClassicUri(uri);

    const origin = this.uriService.getCurrentUri();
    classicUri.addSearch('return_url', origin.toString());

    return classicUri.toString();
  }

  generateCreateProfileRedirectUrl(): string {
    return this.uriService.getCurrentUri().path();
  }

  /**
   * Wrapper method to generate a URL for switching the locale of the page, by appending a lang= param
   * @param {string} locale The locale being switched to
   */
  generateSwitchLocaleUrl(locale: string): string {
    return this.uriService.getCurrentUri().setSearch('lang', locale).toString();
  }

  showAdminMenu(): boolean {
    return this.permissions.hasGlobalPermission(
      Permissions.ACCESS_KEYS,
      Permissions.ADVANCED_MIS,
      Permissions.GRANT_ANY_ROLE,
      Permissions.HOME_MESSAGE,
      Permissions.REQUEST_ROLLOVER,
      Permissions.RESOURCE_METADATA_REFRESH,
      Permissions.UPLOAD_HIERARCHY,
      Permissions.VIEW_ACQUISITIONS,
      Permissions.ADMINISTER_SETTINGS,
    );
  }

  canReviewJobs(): boolean {
    return this.permissions.hasGlobalPermission(
      Permissions.REQUEST_ROLLOVER,
      Permissions.RESOURCE_METADATA_REFRESH,
      Permissions.UPLOAD_HIERARCHY,
    );
  }

  showMisDashboardLink(): boolean {
    return (
      this.permissions.hasGlobalPermission(Permissions.ADVANCED_MIS_VIEW) &&
      this.appConfig.tenant.featureFlags.isMisDashboardEnabled
    );
  }

  /**
   * Toggles the isCollapsed state of the navbar
   **/
  onToggle() {
    this.isCollapsed = !this.isCollapsed;
  }

  /**
   * Log the user in.
   */
  login(): void {
    this.currentUserService.login();
  }

  /**
   * Log the user out, both of Shipshape and RL.
   *
   * The RL logout page has a redirect-to-index behaviour that is desirable here.
   */
  logout(): void {
    this.currentUserService.logout();

    const rlRedirectUrl = new URI(this.appConfig.tenant.httpsBaseUri)
      .segment('/logout.html')
      .toString();

    const logoutUrl = new URI(this.appConfig.personaEndpoint)
      .segment('/auth/logout')
      .query({
        redirectUri: rlRedirectUrl,
      })
      .toString();

    this.redirectionService.redirectToExternalUrl(logoutUrl);
  }
}
